import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["step1", "step2", "industryOption", "buildingTypeContainer"]
  static values = {
    buildingTypes: Object,
    otherTypes: Object
  }

  connect() {
    this.selectedIndustries = []
    this.selectedBuildingTypes = []
    this.selectedOtherTypes = []
    this.expandedIndustries = new Set()

    // 初期選択状態を取得（業種）
    this.industryOptionTargets.forEach(label => {
      const input = label.querySelector("input")
      if (input.checked) {
        this.selectedIndustries.push(input.value)
      }
    })

    // 既存の物件種別選択状態を取得（業種:名前 形式で保存されている）
    const buildingTypeInput = document.getElementById("building-type-input")
    if (buildingTypeInput && buildingTypeInput.value) {
      this.selectedBuildingTypes = buildingTypeInput.value.split(",").map(v => v.trim()).filter(v => v)
    }

    // 既存のその他種別選択状態を取得（業種:名前 形式で保存されている）
    const otherTypeInput = document.getElementById("other-type-input")
    if (otherTypeInput && otherTypeInput.value) {
      this.selectedOtherTypes = otherTypeInput.value.split(",").map(v => v.trim()).filter(v => v)
    }
  }

  open(event) {
    event.preventDefault()

    const dialogId = event.currentTarget.dataset.industryBuildingTypeDialogId
    if (!dialogId) return

    const dialog = document.getElementById(dialogId)
    if (!dialog) return

    dialog.classList.remove("hidden")
    document.body.classList.add("overflow-hidden")
  }

  close() {
    this.element.classList.add("hidden")
    document.body.classList.remove("overflow-hidden")

    // ステップ1に戻す
    this.step1Target.classList.remove("hidden")
    this.step2Target.classList.add("hidden")
  }

  selectIndustry(event) {
    const input = event.target
    const label = input.closest("label")

    if (input.checked) {
      label.classList.add("bg-red-50", "border-red-300")
      if (!this.selectedIndustries.includes(input.value)) {
        this.selectedIndustries.push(input.value)
      }
    } else {
      label.classList.remove("bg-red-50", "border-red-300")
      this.selectedIndustries = this.selectedIndustries.filter(v => v !== input.value)
    }
  }

  goToStep2() {
    if (this.selectedIndustries.length === 0) {
      alert("業種を選択してください")
      return
    }

    // 物件種別選択UIを構築
    this.buildBuildingTypeUI()

    // ステップ切り替え
    this.step1Target.classList.add("hidden")
    this.step2Target.classList.remove("hidden")
  }

  goToStep1() {
    this.step1Target.classList.remove("hidden")
    this.step2Target.classList.add("hidden")
  }

  buildBuildingTypeUI() {
    const container = this.buildingTypeContainerTarget
    container.innerHTML = ""

    this.selectedIndustries.forEach(industry => {
      const buildingTypes = this.buildingTypesValue[industry] || []
      const otherTypes = this.otherTypesValue[industry] || []

      // アコーディオンセクション
      const section = document.createElement("div")
      section.className = "border-b border-gray-200"

      // ヘッダー（業種名）
      const header = document.createElement("button")
      header.type = "button"
      header.className = "w-full flex items-center justify-between py-3 px-2 text-left hover:bg-gray-50"

      const industrySpan = document.createElement("span")
      industrySpan.className = "text-sm font-medium text-gray-900"
      industrySpan.textContent = industry

      const chevronSvg = document.createElementNS("http://www.w3.org/2000/svg", "svg")
      chevronSvg.setAttribute("class", `w-5 h-5 text-gray-400 transform transition-transform ${this.expandedIndustries.has(industry) ? 'rotate-180' : ''}`)
      chevronSvg.setAttribute("data-chevron", "")
      chevronSvg.setAttribute("fill", "none")
      chevronSvg.setAttribute("stroke", "currentColor")
      chevronSvg.setAttribute("viewBox", "0 0 24 24")
      const path = document.createElementNS("http://www.w3.org/2000/svg", "path")
      path.setAttribute("stroke-linecap", "round")
      path.setAttribute("stroke-linejoin", "round")
      path.setAttribute("stroke-width", "2")
      path.setAttribute("d", "M19 9l-7 7-7-7")
      chevronSvg.appendChild(path)

      header.appendChild(industrySpan)
      header.appendChild(chevronSvg)
      header.addEventListener("click", () => this.toggleAccordion(industry, section))

      // コンテンツ（物件種別 + その他種別リスト）
      const content = document.createElement("div")
      content.className = `pl-4 pb-2 ${this.expandedIndustries.has(industry) ? '' : 'hidden'}`
      content.dataset.industryContent = industry

      // 物件種別セクション
      if (buildingTypes.length > 0) {
        const buildingTypeLabel = document.createElement("div")
        buildingTypeLabel.className = "text-xs font-medium text-gray-500 mt-2 mb-1"
        buildingTypeLabel.textContent = "物件種別"
        content.appendChild(buildingTypeLabel)

        buildingTypes.forEach(buildingType => {
          const label = document.createElement("label")
          label.className = "flex items-center justify-between p-2 rounded-sm hover:bg-gray-50 cursor-pointer"

          const isSelected = this.selectedBuildingTypes.includes(`${industry}:${buildingType.name}`)

          const nameDiv = document.createElement("div")
          nameDiv.className = "flex-1"

          const nameSpan = document.createElement("span")
          nameSpan.className = "text-sm font-medium text-gray-900"
          nameSpan.textContent = buildingType.name
          nameDiv.appendChild(nameSpan)

          if (buildingType.description) {
            const descP = document.createElement("p")
            descP.className = "text-xs text-gray-500"
            descP.textContent = buildingType.description
            nameDiv.appendChild(descP)
          }

          const input = document.createElement("input")
          input.type = "checkbox"
          input.name = "building_type_selection"
          input.value = `${industry}:${buildingType.name}`
          input.className = "h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded-sm ml-2"
          input.checked = isSelected
          input.addEventListener("change", (e) => this.selectBuildingType(e))

          label.appendChild(nameDiv)
          label.appendChild(input)
          content.appendChild(label)
        })
      }

      // その他種別セクション
      if (otherTypes.length > 0) {
        const otherTypeLabel = document.createElement("div")
        otherTypeLabel.className = "text-xs font-medium text-gray-500 mt-4 mb-1"
        otherTypeLabel.textContent = "その他種別"
        content.appendChild(otherTypeLabel)

        otherTypes.forEach(otherType => {
          const label = document.createElement("label")
          label.className = "flex items-center justify-between p-2 rounded-sm hover:bg-gray-50 cursor-pointer"

          const isSelected = this.selectedOtherTypes.includes(`${industry}:${otherType.name}`)

          const nameDiv = document.createElement("div")
          nameDiv.className = "flex-1"

          const nameSpan = document.createElement("span")
          nameSpan.className = "text-sm font-medium text-gray-900"
          nameSpan.textContent = otherType.name
          nameDiv.appendChild(nameSpan)

          if (otherType.description) {
            const descP = document.createElement("p")
            descP.className = "text-xs text-gray-500"
            descP.textContent = otherType.description
            nameDiv.appendChild(descP)
          }

          const input = document.createElement("input")
          input.type = "checkbox"
          input.name = "other_type_selection"
          input.value = `${industry}:${otherType.name}`
          input.className = "h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded-sm ml-2"
          input.checked = isSelected
          input.addEventListener("change", (e) => this.selectOtherType(e))

          label.appendChild(nameDiv)
          label.appendChild(input)
          content.appendChild(label)
        })
      }

      section.appendChild(header)
      section.appendChild(content)
      container.appendChild(section)

      // デフォルトで展開
      if (!this.expandedIndustries.has(industry)) {
        this.expandedIndustries.add(industry)
        content.classList.remove("hidden")
        header.querySelector("[data-chevron]").classList.add("rotate-180")
      }
    })
  }

  toggleAccordion(industry, section) {
    const content = section.querySelector(`[data-industry-content="${industry}"]`)
    const chevron = section.querySelector("[data-chevron]")

    if (this.expandedIndustries.has(industry)) {
      this.expandedIndustries.delete(industry)
      content.classList.add("hidden")
      chevron.classList.remove("rotate-180")
    } else {
      this.expandedIndustries.add(industry)
      content.classList.remove("hidden")
      chevron.classList.add("rotate-180")
    }
  }

  selectBuildingType(event) {
    const input = event.target
    const value = input.value

    if (input.checked) {
      if (!this.selectedBuildingTypes.includes(value)) {
        this.selectedBuildingTypes.push(value)
      }
    } else {
      this.selectedBuildingTypes = this.selectedBuildingTypes.filter(v => v !== value)
    }
  }

  selectOtherType(event) {
    const input = event.target
    const value = input.value

    if (input.checked) {
      if (!this.selectedOtherTypes.includes(value)) {
        this.selectedOtherTypes.push(value)
      }
    } else {
      this.selectedOtherTypes = this.selectedOtherTypes.filter(v => v !== value)
    }
  }

  confirm() {
    // hidden inputの値を更新
    const industryInput = document.getElementById("industry-input")
    const buildingTypeInput = document.getElementById("building-type-input")
    const otherTypeInput = document.getElementById("other-type-input")

    if (industryInput) {
      industryInput.value = this.selectedIndustries.join(",")
    }

    if (buildingTypeInput) {
      // 業種:物件種別形式で保存
      buildingTypeInput.value = this.selectedBuildingTypes.join(",")
    }

    if (otherTypeInput) {
      // 業種:その他種別形式で保存
      otherTypeInput.value = this.selectedOtherTypes.join(",")
    }

    // 統合された表示を更新
    const detailDisplay = document.getElementById("industry-detail-display")
    if (detailDisplay) {
      detailDisplay.innerHTML = this.buildIntegratedDisplay()
    }

    this.close()

    // カスタムイベントを発行
    this.dispatch("selected", {
      detail: {
        industries: this.selectedIndustries,
        buildingTypes: this.selectedBuildingTypes,
        otherTypes: this.selectedOtherTypes
      }
    })
  }

  // 業種ごとに物件種別・その他種別をまとめた表示を生成（DOM API使用）
  buildIntegratedDisplay() {
    const container = document.createElement("div")

    if (this.selectedIndustries.length === 0) {
      const emptySpan = document.createElement("span")
      emptySpan.className = "text-sm text-gray-400"
      emptySpan.textContent = "未設定"
      container.appendChild(emptySpan)
      return container.innerHTML
    }

    // 物件種別を業種ごとにグループ化
    const buildingTypesByIndustry = this.groupItemsByIndustry(this.selectedBuildingTypes)
    // その他種別を業種ごとにグループ化
    const otherTypesByIndustry = this.groupItemsByIndustry(this.selectedOtherTypes)

    // 業種ごとに要素を生成
    this.selectedIndustries.forEach(industry => {
      const buildingTypes = buildingTypesByIndustry[industry] || []
      const otherTypes = otherTypesByIndustry[industry] || []

      const industryDiv = document.createElement("div")
      industryDiv.className = "mb-3"

      const industryBadge = document.createElement("span")
      industryBadge.className = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
      industryBadge.textContent = industry
      industryDiv.appendChild(industryBadge)

      const contentDiv = document.createElement("div")
      contentDiv.className = "ml-4 mt-1 v3-space-y-1"

      if (buildingTypes.length > 0) {
        const buildingTypeRow = document.createElement("div")
        const labelSpan = document.createElement("span")
        labelSpan.className = "text-xs text-gray-500"
        labelSpan.textContent = "物件種別:"
        buildingTypeRow.appendChild(labelSpan)

        buildingTypes.forEach(name => {
          const badge = document.createElement("span")
          badge.className = "inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-medium bg-blue-50 text-blue-700 ml-1"
          badge.textContent = name
          buildingTypeRow.appendChild(badge)
        })
        contentDiv.appendChild(buildingTypeRow)
      }

      if (otherTypes.length > 0) {
        const otherTypeRow = document.createElement("div")
        const labelSpan = document.createElement("span")
        labelSpan.className = "text-xs text-gray-500"
        labelSpan.textContent = "その他種別:"
        otherTypeRow.appendChild(labelSpan)

        otherTypes.forEach(name => {
          const badge = document.createElement("span")
          badge.className = "inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-medium bg-green-50 text-green-700 ml-1"
          badge.textContent = name
          otherTypeRow.appendChild(badge)
        })
        contentDiv.appendChild(otherTypeRow)
      }

      if (buildingTypes.length === 0 && otherTypes.length === 0) {
        const emptySpan = document.createElement("span")
        emptySpan.className = "text-xs text-gray-400"
        emptySpan.textContent = "物件種別・その他種別 未設定"
        contentDiv.appendChild(emptySpan)
      }

      industryDiv.appendChild(contentDiv)
      container.appendChild(industryDiv)
    })

    return container.innerHTML
  }

  // アイテムを業種ごとにグループ化
  groupItemsByIndustry(items) {
    const grouped = {}
    items.forEach(item => {
      const parts = item.split(":")
      if (parts.length === 2) {
        const [industry, name] = parts
        if (!grouped[industry]) {
          grouped[industry] = []
        }
        grouped[industry].push(name)
      }
    })
    return grouped
  }
}
